import axios from "axios";
import i18n from "@/i18n";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({ baseURL: API, withCredentials: true });

function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  const token = getCsrfToken();
  if (token && ["post", "put", "patch", "delete"].includes(method)) {
    config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

api.interceptors.response.use(undefined, (error) => {
  if (error?.response?.status === 402 && !window.location.pathname.startsWith("/abonnement")) {
    window.location.href = "/abonnement";
  }
  return Promise.reject(error);
});

const LOCALES = { fr: "fr-FR", en: "en-GB", es: "es-ES" };
export const currentLocale = () => LOCALES[(i18n.language || "fr").slice(0, 2)] || "fr-FR";

export const eur = (n) =>
  new Intl.NumberFormat(currentLocale(), { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export const dateFr = (d) => (d ? new Date(d).toLocaleDateString(currentLocale()) : "");

export const dateTimeFr = (d) =>
  d ? new Date(d).toLocaleDateString(currentLocale(), { day: "2-digit", month: "2-digit" }) +
      ` ${i18n.t("commun.a")} ` + new Date(d).toLocaleTimeString(currentLocale(), { hour: "2-digit", minute: "2-digit" }) : "";

export function apiError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return fallback || i18n.t("commun.erreur_generique");
}

export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const QUEUE_KEY = "chantiersplus_offline_devis";

export function getOfflineQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function queueOfflineDevis(payload) {
  const q = getOfflineQueue();
  q.push({ ...payload, queued_at: new Date().toISOString() });
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

export async function syncOfflineDevis() {
  const q = getOfflineQueue();
  if (!q.length) return 0;
  const rest = [];
  let synced = 0;
  for (const item of q) {
    const { queued_at, ...payload } = item;
    try {
      await api.post("/devis", payload);
      synced++;
    } catch (e) {
      if (!e?.response) rest.push(item);
    }
  }
  localStorage.setItem(QUEUE_KEY, JSON.stringify(rest));
  return synced;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from([...atob(base64)].map((c) => c.charCodeAt(0)));
}

export async function registerPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return false;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const { data } = await api.get("/push/vapid-public-key");
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    await api.post("/push/subscription", sub.toJSON());
    return true;
  } catch {
    return false;
  }
}
